<template>
  <div>
    <InputField
      id="storageName"
      v-model.trim="name"
      label="Name"
      :error="error.name"
      class="storage__field"
      label-info-tooltip="New name for the setting."
    />

    <InputField
      id="storageBucket"
      v-model.trim="bucket"
      label="S3 Bucket"
      :error="error.bucket"
      class="storage__field"
      label-info-tooltip="Name of the storage bucket / storage account name.
        In case of AWS S3, please don't use full ARN, just the name of the bucket."
    />

    <InputField
      v-model.trim="prefix"
      label="S3 Prefix"
      :error="error.prefix"
      class="storage__field"
      label-info-tooltip="Prefix for all Darwin-created files (like thumbnails).
        It cannot start nor end with '/'. Examples: 'v7labs', 'mycontainer/darwin'."
    />

    <S3RegionSelect
      :value="region"
      label="S3 Region"
      provider="aws"
      :error="error.region"
      class="storage__field"
      @input="region = $event"
    />

    <InputField
      v-model.trim="cloudfrontHost"
      label="CloudFront Host"
      :error="error.cloudfrontHost"
      class="storage__field"
      label-info-tooltip="Hostname of your CloudFront distribution which proxies requests to the S3
        bucket. This is an advanced feature meant to speed up access to your files when
        the S3 Bucket is geographically far away from your Annotators.
        Example: 'https://xyzmydistroxyz.cloudfront.net'."
      label-info-link="https://docs.v7labs.com/docs/configuring-cloudfront-distribution"
      label-info-link-text="Read more"
    />

    <RadioGroup
      class="storage__field"
      :options="readOnlyOptions"
      :value="isReadonly"
      size="md"
      @change="isReadonly = $event"
    />

    <InputField
      v-if="isReadonlyStorage"
      v-model.trim="filepath"
      label="Validation File Path"
      :error="error.filepath"
      class="storage__field"
      placeholder="v7_test.txt"
      label-info-tooltip="Optional file path for read-only validation. If not specified, we'll look for 'v7_test.txt' in the bucket root to verify access. This file should exist in your storage bucket."
    />

    <Divider class="divider" />

    <div class="control__toggle">
      <Toggle
        v-model="isDefault"
        kind="primary"
        size="small"
      >
        Set as default
      </Toggle>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'AwsStorageForm',
}
</script>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import isEmpty from 'validator/lib/isEmpty'
import InputField from '@/components/Common/InputField/V2/InputField.vue'
import S3RegionSelect from '@/components/Storage/S3RegionSelect.vue'
import type { StoragePayload } from '@/store/types'
import Toggle from '@/uiKit/Toggle/Toggle.vue'
import RadioGroup from '@/uiKit/RadioGroup/RadioGroup.vue'
import Divider from '@/uiKit/Divider/Divider.vue'
import { readOnlyOptions } from './data'

type AwsStorageFormError = {
  name?: string
  bucket?: string
  prefix?: string
  region?: string
  cloudfrontHost?: string
  filepath?: string
}

const props = withDefaults(
  defineProps<{
    value?: StoragePayload | null
  }>(),
  { value: null },
)

const emit = defineEmits<{
  (e: 'submitted', payload: StoragePayload): void
}>()

const name = ref('')
const bucket = ref('')
const prefix = ref('')
const region = ref('us-east-2')
const cloudfrontHost = ref('')
const filepath = ref('')
const isReadonly = ref('0')
const isDefault = ref(false)
const error = ref<AwsStorageFormError>({})

const isReadonlyStorage = computed(() => isReadonly.value === '1')

onMounted(() => {
  if (props.value) {
    name.value = props.value.name
    bucket.value = props.value.bucket
    prefix.value = props.value.prefix || ''
    region.value = props.value.region || 'us-east-2'
    cloudfrontHost.value = props.value.cloudfront_host || ''
    filepath.value = props.value.filepath || ''
    isReadonly.value = props.value.readonly ? '1' : '0'
    isDefault.value = props.value.default
  }
})

const validateForm = (): boolean => {
  error.value = {}

  if (isEmpty(name.value.trim())) {
    error.value.name = 'Storage name cannot be empty'
  }

  const bucketValue = bucket.value.trim()
  if (isEmpty(bucketValue)) {
    error.value.bucket = 'Bucket cannot be empty'
    // eslint-disable-next-line no-useless-escape
  } else if (!bucketValue.match(/^[a-z0-9][a-z0-9\-]{1,61}[a-z0-9]$/)) {
    error.value.bucket = 'Invalid bucket name'
  }

  const trimPrefix = prefix.value.trim()
  if (
    trimPrefix.length &&
    (trimPrefix.charAt(0) === '/' || trimPrefix.charAt(trimPrefix.length - 1) === '/')
  ) {
    error.value.prefix = "Prefix cannot start or end with '/'"
  }

  if (isEmpty(region.value.trim())) {
    error.value.region = 'Region cannot be empty'
  }

  let cloudfrontHostValue = cloudfrontHost.value.trim()
  if (!isEmpty(cloudfrontHostValue)) {
    let cloudfrontHostUrl = null
    try {
      cloudfrontHostUrl = new URL(cloudfrontHostValue)
    } catch {
      try {
        cloudfrontHostUrl = new URL('https://' + cloudfrontHostValue)
      } catch {
        error.value.cloudfrontHost = 'Invalid URL'
      }
    }

    if (cloudfrontHostUrl != null) {
      cloudfrontHost.value = cloudfrontHostUrl.origin
      if (!['https:', 'http:'].includes(cloudfrontHostUrl.protocol)) {
        error.value.cloudfrontHost = 'Invalid protocol'
      } else if (cloudfrontHostUrl.pathname != '/') {
        error.value.cloudfrontHost = 'Host cannot include path'
      }
    }
  }

  return Object.keys(error.value).length === 0
}

const onSubmit = (): void => {
  if (!validateForm()) {
    return
  }

  emit('submitted', {
    slug: props.value?.slug || '',
    name: name.value.trim(),
    readonly: isReadonly.value === '1',
    bucket: bucket.value.trim(),
    prefix: prefix.value.trim(),
    region: region.value,
    cloudfront_host: cloudfrontHost.value.trim(),
    filepath: filepath.value.trim() || undefined,
    default: isDefault.value,
  })
}

defineExpose({ onSubmit })
</script>

<style lang="scss" scoped>
@import './FormBase.scss';
</style>
